import React, { useState, useEffect } from 'react';

const AssessmentLoadingModal = ({ isSubmitting, category = "Software Development / Engineering" }) => {
  // Did you know facts for loading modal
  const didYouKnowFacts = {
    "Data & Analytics": [
      "The world creates over 328 million terabytes of data every single day.",
      "By 2025, nearly 30% of the world's data will require real-time processing.",
      "90% of the world's data was generated in just the last two years.",
      "Poor data quality costs companies an average of $12.9 million per year.",
      "Dark data makes up around 55-65% of all data in organizations.",
      "Data scientists were dubbed the 'sexiest job of the 21st century' by HBR.",
      "80% of a data analyst's time is spent cleaning data, not analyzing it.",
      "The first known data visualization was in the 1780s, showing Napoleon's campaign.",
      "More than 60% of executives struggle to turn data insights into action.",
      "Google processes over 3.5 billion searches per day - pure data at work.",
      "The first hard drive in 1956 stored just 5MB of data and weighed a ton.",
      "Every person on Earth creates about 1.7MB of data per second.",
      "AI models are trained on petabytes of structured and unstructured data.",
      "More data has been created in the last decade than in all of human history before.",
      "By 2030, the big data market is expected to surpass $400 billion globally.",
      "Companies that use big data effectively can increase profits by 8-10%.",
      "Spotify's 'Discover Weekly' is powered by machine learning on massive datasets.",
      "90% of corporate strategies now list data as a critical enterprise asset.",
      "The term 'data scientist' only became mainstream around 2008.",
      "Data breaches cost companies an average of $4.45 million in 2023."
    ],
    "IT & Infrastructure": [
      "The first 1GB hard drive in 1980 weighed 550 pounds and cost $40,000.",
      "Google's data centers consume enough energy to power 200,000 homes annually.",
      "94% of enterprises now use cloud services.",
      "The average cost of IT downtime is $5,600 per minute.",
      "Microsoft's 'blue screen of death' message was first written by an intern.",
      "The first email ever sent in 1971 just said 'QWERTYUIOP'.",
      "Quantum computers can solve problems in seconds that would take millennia.",
      "Over 1 billion devices run Windows worldwide.",
      "90% of businesses still rely on COBOL for core systems.",
      "The average lifespan of a corporate server is only 3-5 years.",
      "Amazon Web Services launched in 2006, now dominates global cloud.",
      "The internet runs on over 75 million connected servers.",
      "A single data center outage can cost companies millions per hour.",
      "More than 70% of IT budgets are spent on infrastructure maintenance.",
      "In 1999, Salesforce launched the first widely known SaaS model.",
      "IPv6 supports 340 undecillion unique IP addresses.",
      "The Apollo moon landing computers had less power than a modern smartphone.",
      "Fiber optic cables are thinner than a human hair but carry terabits per second.",
      "Most cyberattacks exploit unpatched IT infrastructure vulnerabilities.",
      "The global IT services market is projected to exceed $1 trillion by 2027."
    ],
    "Software Development / Engineering": [
      "NASA still uses code written in the 1970s for some space missions.",
      "The term 'Hello, World' appeared in a C tutorial in 1974.",
      "GitHub hosts more than 420 million public repositories.",
      "The first 'computer bug' was literally a moth in a Harvard Mark II computer.",
      "Developers spend around 42% of their time fixing bugs.",
      "The Google homepage originally had a spelling mistake in 'Google'.",
      "The first version of Windows (1985) took 45 floppy disks to install.",
      "JavaScript is the most commonly used programming language in the world.",
      "Python overtook Java as the most taught programming language in universities.",
      "Google's first server was built using LEGO bricks.",
      "The average software project runs 66% over budget.",
      "The Linux operating system runs on over 96% of the top 1 million servers.",
      "The iPhone's original operating system contained about 2 million lines of code.",
      "Facebook was originally written entirely in PHP.",
      "Microsoft's first product was a BASIC interpreter for the Altair 8800.",
      "Open-source contributions are valued in the billions of dollars annually.",
      "COBOL developers remain in high demand due to aging infrastructure.",
      "Some modern cars contain over 100 million lines of code.",
      "The video game industry drives major advances in software development tools.",
      "Agile methodology was officially introduced in 2001 with the Agile Manifesto.",
      "JavaScript was created in just 10 days by Brendan Eich in 1995?",
      "The first computer bug was an actual bug - a moth trapped in a Harvard Mark II computer in 1947?",
      "Python was named after the British comedy group Monty Python?",
      "The term 'debugging' was coined by Admiral Grace Hopper?",
      "CSS stands for Cascading Style Sheets and was first proposed in 1994?",
      "HTML was invented by Tim Berners-Lee in 1990 as part of the World Wide Web?",
      "The '@' symbol in email addresses was chosen by Ray Tomlinson in 1971?",
      "React was created by Facebook and open-sourced in 2013?",
      "Git was created by Linus Torvalds in just 2 weeks in 2005?",
      "The first website ever created is still online at info.cern.ch?",
      "Stack Overflow was founded in 2008 and now has over 50 million monthly visitors?",
      "The term 'cookie' in web development comes from 'magic cookie', a Unix term?"      
    ],
    "Product Management & Strategy": [
      "The first product manager role was created at Procter & Gamble in 1931.",
      "Half of all product launches fail to meet business goals.",
      "42% of startups fail due to lack of market need.",
      "Amazon deploys new features every 11.6 seconds on average.",
      "The term 'MVP' (minimum viable product) was popularized by Eric Ries in 2011.",
      "Over 50% of PMs don't come from technical backgrounds.",
      "Excel is still one of the most common PM tools.",
      "Netflix's recommendation engine drives 80% of what users watch.",
      "Most PMs spend more time firefighting than strategic planning.",
      "The iPod pitch was '1,000 songs in your pocket.'",
      "Great PMs are often called 'mini CEOs' of their product.",
      "More than 70% of product decisions involve customer feedback loops.",
      "The Lean Startup methodology transformed how PMs think about iteration.",
      "By 2028, demand for product managers is expected to rise by 30%.",
      "OKRs (Objectives & Key Results) originated at Intel in the 1970s.",
      "The Kano model has been used since the 1980s to prioritize features.",
      "A/B testing was first used in the 1920s in agriculture.",
      "Tesla launches product features directly via software updates to cars.",
      "Design thinking is a core skill PMs use for innovation.",
      "Product-market fit is often described as 'customers pulling the product out of you.'"
    ],
    "Human Resources & Talent Acquisition": [
      "The first HR department was created in 1901 at NCR after a strike.",
      "75% of resumes are filtered by ATS before a human sees them.",
      "Employees referred by others are 4x more likely to be hired.",
      "Onboarding well improves retention by 82%.",
      "The average cost per hire in the US is $4,700.",
      "By 2025, 75% of the workforce will be Millennials or Gen Z.",
      "The world's longest job interview lasted 67 days.",
      "Diverse teams outperform by up to 36% in profitability.",
      "HR tech is a $35B global industry.",
      "9 in 10 employees prefer flexibility in work.",
      "The average recruiter spends only 6-7 seconds scanning a resume.",
      "Google once got over 3 million applications in a single year.",
      "Companies with engaged employees are 21% more profitable.",
      "Friday is the most popular day for job offers.",
      "Over 70% of job seekers use mobile devices to apply.",
      "One bad hire can cost a company over $50,000.",
      "Remote jobs receive 7x more applications than in-office ones.",
      "Glassdoor reports that culture and values matter more than salary for many employees.",
      "AI is now used to screen candidates in over 55% of Fortune 500 companies.",
      "HR analytics is one of the fastest-growing fields in people operations."
    ],
    "Sales & Business Development": [
        "The word 'salesman' first appeared in print in 1523.",
        "80% of sales require 5 follow-up calls, but 44% of reps give up after one.",
        "Top-performing salespeople spend 60% more time on research than others.",
        "People remember 10% of what they hear, but 90% of what they experience - experiential selling matters.",
        "More than 70% of B2B buyers fully define their needs before speaking to sales.",
        "The average email open rate for sales outreach is only about 18%.",
        "Referrals close 70% faster and have 69% higher conversion rates.",
        "Salesforce was originally called 'Upstartle' before being renamed.",
        "Over 50% of sales happen after the fifth contact - persistence wins.",
        "Only 13% of customers believe a salesperson truly understands their needs.",
        "LinkedIn is used by 89% of top sales professionals for prospecting.",
        "The best time to call a lead is between 4-5 p.m. on Wednesdays or Thursdays.",
        "Storytelling increases sales conversion by up to 30%.",
        "The average B2B deal involves 6 to 10 decision-makers.",
        "Salespeople who use social selling outsell peers by 78%.",
        "Over 40% of salespeople say prospecting is the hardest part of their job.",
        "The term 'cold call' dates back to the early 20th century.",
        "CRM adoption can boost sales by up to 29%.",
        "Emotionally connected customers are 3x more likely to buy and recommend.",
        "Artificial intelligence is now used by 50% of sales teams to predict deal success."
      ],
      "Customer Success & Support": [
        "It costs 5x more to acquire a new customer than to retain an existing one.",
        "Increasing customer retention by just 5% can boost profits by up to 95%.",
        "86% of buyers are willing to pay more for a great customer experience.",
        "The first recorded customer service complaint dates back to 1750 BC in ancient Babylon.",
        "The term 'customer success' was first coined by Salesforce in 2005.",
        "Companies that prioritize customer success see 1.5x faster growth.",
        "Nearly 90% of customers expect an immediate response to support questions.",
        "More than 60% of customers have stopped buying from a brand after one bad experience.",
        "Chatbots now handle over 70% of routine customer queries.",
        "Support agents using empathy-based communication resolve issues 25% faster.",
        "A 1-star improvement in online reviews can increase revenue by up to 9%.",
        "Customer churn costs U.S. businesses over $100 billion annually.",
        "The average customer tells 9 people about a good experience - and 16 about a bad one.",
        "Netflix and Zoom have dedicated 'Customer Success Engineers' to prevent churn.",
        "The average Net Promoter Score (NPS) for SaaS companies is around 30.",
        "Companies with strong onboarding reduce churn by 63%.",
        "The world's first call center opened in 1965 in the UK.",
        "Customer support via social media can improve satisfaction by 25%.",
        "80% of customers say the experience a company provides is as important as its products.",
        "AI-driven support systems can reduce ticket resolution time by up to 40%."
      ]    
  };  

  // Get facts for the specified category, fallback to default if category doesn't exist
  const categoryFacts = didYouKnowFacts[category] || didYouKnowFacts["Software Development / Engineering"];
  
  // State for rotating facts
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [isFactVisible, setIsFactVisible] = useState(true);

  // Rotate facts every few seconds with fade transition during submission
  useEffect(() => {
    let interval;
    if (isSubmitting) {
      interval = setInterval(() => {
        // Fade out
        setIsFactVisible(false);
        
        // After fade out completes, change fact and fade in
        setTimeout(() => {
          // Generate random index, ensuring it's different from current one
          let newIndex;
          do {
            newIndex = Math.floor(Math.random() * categoryFacts.length);
          } while (newIndex === currentFactIndex && categoryFacts.length > 1);
          
          setCurrentFactIndex(newIndex);
          setIsFactVisible(true);
        }, 300); // 300ms fade out duration
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isSubmitting, categoryFacts.length, currentFactIndex]);

  
  // Temporarily always show for testing - change back to: if (!isSubmitting) return null;
  // if (false) return null;
  if (!isSubmitting) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-md flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-12 max-w-2xl w-full mx-4 text-center">
        {/* Loading Animation - Custom CSS Animation */}
        <div className="p-8">
          <div className="loader mx-auto mb-2"></div>
        </div>
        
        {/* Assessing Text */}
        <h3 className="text-2xl font-regular text-gray-800 mb-12">Assessing ...</h3>
        
        {/* Did You Know Section - No border */}
        <div className="p-2 min-h-[130px]">
          <p className="text-xl font-regular text-gray-800 mb-4 tracking-wide">💡 Did you know?</p>
          <p className={`text-xl text-gray-700 leading-relaxed font-medium transition-opacity duration-300 ${
            isFactVisible ? 'opacity-100' : 'opacity-0'
          }`}>
            {categoryFacts[currentFactIndex]}
          </p>
        </div>
      </div>
      
      <style jsx="true">{`
        .loader {
          width: 4px;
          color: #000;
          aspect-ratio: 1;
          border-radius: 50%;
          box-shadow: 
            19px -19px 0 0px, 38px -19px 0 0px, 57px -19px 0 0px,
            19px 0     0 5px, 38px 0     0 5px, 57px 0     0 5px,
            19px 19px  0 0px, 38px 19px  0 0px, 57px 19px  0 0px;
          transform: translateX(-38px);
          animation: l26 4s infinite linear;
        }
        @keyframes l26 {
          12.5% {
            box-shadow: 
              19px -19px 0 0px, 38px -19px 0 0px, 57px -19px 0 5px,
              19px 0     0 5px, 38px 0     0 0px, 57px 0     0 5px,
              19px 19px  0 0px, 38px 19px  0 0px, 57px 19px  0 0px;
          }
          25% {
            box-shadow: 
              19px -19px 0 5px, 38px -19px 0 0px, 57px -19px 0 5px,
              19px 0     0 0px, 38px 0     0 0px, 57px 0     0 0px,
              19px 19px  0 0px, 38px 19px  0 5px, 57px 19px  0 0px;
          }
          50% {
            box-shadow: 
              19px -19px 0 5px, 38px -19px 0 5px, 57px -19px 0 0px,
              19px 0     0 0px, 38px 0     0 0px, 57px 0     0 0px,
              19px 19px  0 0px, 38px 19px  0 0px, 57px 19px  0 5px;
          }
          62.5% {
            box-shadow: 
              19px -19px 0 0px, 38px -19px 0 0px, 57px -19px 0 0px,
              19px 0     0 5px, 38px 0     0 0px, 57px 0     0 0px,
              19px 19px  0 0px, 38px 19px  0 5px, 57px 19px  0 5px;
          }
          75% {
            box-shadow: 
              19px -19px 0 0px, 38px -19px 0 5px, 57px -19px 0 0px,
              19px 0     0 0px, 38px 0     0 0px, 57px 0     0 5px,
              19px 19px  0 0px, 38px 19px  0 0px, 57px 19px  0 5px;
          }
          87.5% {
            box-shadow: 
              19px -19px 0 0px, 38px -19px 0 5px, 57px -19px 0 0px,
              19px 0     0 0px, 38px 0     0 5px, 57px 0     0 0px,
              19px 19px  0 5px, 38px 19px  0 0px, 57px 19px  0 0px;
          }
        }
      `}</style>
    </div>
  );
};

export default AssessmentLoadingModal;
